import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { MessageSquareText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n";
import { api, errMsg, todayStr, fmtDate } from "@/lib/api";
import { Card, TimeInput } from "@/components/kv";

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-900",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

export const ReqStatus = ({ status, testid }) => {
  const { t } = useT();
  return <span data-testid={testid} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[status]}`}>{t(`req.${status}`)}</span>;
};

const MyRequests = ({ items }) => {
  const { t, lang } = useT();
  if (!items.length) return null;
  return (
    <div className="mt-5 pt-4 border-t border-[#E8DFD0]" data-testid="my-requests">
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">{t("req.mine")}</div>
      <ul className="space-y-2">
        {items.slice(0, 5).map((r) => (
          <li key={r.id} className="rounded-xl bg-[#FAF6F0] p-3 text-sm" data-testid={`my-request-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold capitalize">{fmtDate(r.date, lang)}</span>
              <ReqStatus status={r.status} testid={`my-request-status-${r.id}`} />
            </div>
            {(r.requested_in || r.requested_out) && (
              <div className="font-mono-kv text-xs text-stone-600 mt-1">{r.requested_in || "—"} – {r.requested_out || "—"}</div>
            )}
            <p className="text-stone-700 mt-1 break-words">{r.message}</p>
            {r.admin_note && <p className="text-xs text-stone-500 mt-1 italic">{t("req.admin_note")}: {r.admin_note}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RequestBox = () => {
  const { t } = useT();
  const [f, setF] = useState({ date: todayStr(), requested_in: "", requested_out: "", message: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/me/requests").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/me/requests", { ...f, requested_in: f.requested_in || null, requested_out: f.requested_out || null });
      toast.success(t("req.sent"));
      setF({ date: todayStr(), requested_in: "", requested_out: "", message: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="request-box">
      <div className="flex items-center gap-2">
        <MessageSquareText className="h-5 w-5 text-[#b8563a]" />
        <h2 className="font-display text-2xl font-semibold">{t("req.box_title")}</h2>
      </div>
      <p className="text-sm text-stone-500 mt-1 mb-4">{t("req.box_hint")}</p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="space-y-1 col-span-2 sm:col-span-1">
            <Label className="text-xs">{t("common.date")}</Label>
            <Input type="date" max={todayStr()} value={f.date} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} className="bg-white" data-testid="request-date-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("req.real_in")} <span className="text-stone-400">({t("req.optional")})</span></Label>
            <TimeInput value={f.requested_in} onChange={(v) => setF((s) => ({ ...s, requested_in: v }))} className="bg-white font-mono-kv w-full" data-testid="request-in-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("req.real_out")} <span className="text-stone-400">({t("req.optional")})</span></Label>
            <TimeInput value={f.requested_out} onChange={(v) => setF((s) => ({ ...s, requested_out: v }))} className="bg-white font-mono-kv w-full" data-testid="request-out-input" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("req.message")}</Label>
          <Textarea rows={3} maxLength={500} value={f.message} placeholder={t("req.placeholder")}
            onChange={(e) => setF((s) => ({ ...s, message: e.target.value }))} className="bg-white" data-testid="request-message-input" />
        </div>
        <Button className="rounded-full w-full sm:w-auto px-6" onClick={send} disabled={busy || f.message.trim().length < 3 || !f.date} data-testid="request-send-button">
          <Send className="h-4 w-4 mr-1.5" />{t("req.send")}
        </Button>
      </div>
      <MyRequests items={items} />
    </Card>
  );
};
